import { Link } from "react-router-dom";
import { Reveal } from "@/components/common/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/cn";

const NAV_LINKS = [
  { label: "Architecture", hash: "#architecture", dotColor: "bg-sky-500" },
  { label: "Infrastructure", hash: "#infrastructure", dotColor: "bg-teal-500" },
  { label: "Governance", hash: "#governance", dotColor: "bg-purple-500" },
  { label: "Security", hash: "#security", dotColor: "bg-emerald-500" },
];

/* ── Hex geometry ── */
const HEX_CLIP = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

type HexVariant = "ghost" | "service" | "agent";

interface HexCell {
  label?: string;
  sub?: string;
  dotColor?: string;
  href?: string;
  variant: HexVariant;
}

// Shorthand helpers
const g: HexCell = { variant: "ghost" };
const s = (label: string, sub: string): HexCell => ({ variant: "service", label, sub });
const a = (label: string, sub: string, href: string): HexCell => ({
  variant: "agent", label, sub, href,
});

/*
 * Layout concept (center → outward):
 *   Agents → Business services → Tech infrastructure → Methodology/practices
 *
 * Wings repeat outward to fill any viewport.
 */

// ── Wings: tech infra + methodology (outermost, repeating) ──
const WING_0: HexCell[] = [
  g, s("Terraform", "IaC"), s("SSOT", "Pattern"), g, s("GitOps", "Workflow"),
  s("eBPF", "Kernel"), g, s("OPA", "Policy"), s("12-Factor", "Method"), g,
];
const WING_1: HexCell[] = [
  s("K8s", "Orchestrate"), g, s("DDD", "Design"), s("Service Mesh", "Istio"),
  g, s("CQRS", "Pattern"), s("gRPC", "Protocol"), g, s("Saga", "Pattern"),
  s("BDD", "Testing"),
];
const WING_2: HexCell[] = [
  g, s("Event Bus", "Kafka"), s("TDD", "Practice"), g, s("Envoy", "Proxy"),
  s("CI/CD", "Pipeline"), g, s("IaC", "Pulumi"), s("Zero Trust", "Security"), g,
];
const WING_3: HexCell[] = [
  s("Cilium", "Network"), g, s("CNCF", "Stack"), s("OpenTel", "Observe"),
  g, s("SRE", "Practice"), s("Chaos", "Engineer"), g, s("CDC", "Contract"),
  s("Trunk", "Dev"),
];

// ── Center: Business (inner ring) + Agents (core) ──

// Row 0 — methodology / architecture (faintest)
const CENTER_0: HexCell[] = [
  s("SSOT", "Pattern"), s("Event-Driven", "Arch"), g, s("Microservice", "Arch"),
  s("AI Gateway", "Router"), g, s("Domain Model", "DDD"), s("API-First", "Design"),
];
// Row 1 — business services
const CENTER_1: HexCell[] = [
  s("SSO", "Identity"), s("Encryption", "AES-256"), s("Compliance", "Gates"),
  s("Billing", "Metering"), s("Audit", "Trails"), s("Data", "Residency"),
  s("RBAC", "Access"),
];
// Row 2 — agents (focal point), XENI centered
const CENTER_2: HexCell[] = [
  s("Queue", "Events"), s("Oversight", "Human"),
  a("RobolabX", "Launching", "/products#robolabx"),
  a("XENI", "Live", "/products#xeni"),
  a("Metaroom", "Next", "/products#metaroom"),
  s("Monitor", "Alerts"), s("Webhook", "Events"),
];
// Row 3 — business services
const CENTER_3: HexCell[] = [
  s("KYC", "Verify"), s("Ledger", "Finance"), s("Workflow", "Engine"),
  s("Vault", "Secrets"), s("Storage", "Object"), s("Search", "Index"),
  s("Cache", "Redis"),
];

// Build full rows: repeat wings on both sides of center
const REPEATS = 3;

function buildRow(wing: HexCell[], center: HexCell[]): HexCell[] {
  const leftWing: HexCell[] = [];
  for (let i = 0; i < REPEATS; i++) leftWing.push(...[...wing].reverse());
  const rightWing: HexCell[] = [];
  for (let i = 0; i < REPEATS; i++) rightWing.push(...wing);
  return [...leftWing, ...center, ...rightWing];
}

const ROWS = [
  buildRow(WING_0, CENTER_0),
  buildRow(WING_1, CENTER_1),
  buildRow(WING_2, CENTER_2),
  buildRow(WING_3, CENTER_3),
];

/* ── Opacity & delay based on distance from center ── */
const CENTER_ROW = 2;
// Focal point is row 2 (agents). Symmetric-ish falloff, bottom slightly more present.
const ROW_OPACITY = [0.1, 0.3, 0.55, 0.2];

// Horizontal fade: starts sooner for a gentler falloff across the viewport
const FADE_START = 3;
const FADE_END = 10;

function getOpacity(ri: number, ci: number, rowLen: number): number {
  const center = (rowLen - 1) / 2;
  const dist = Math.abs(ci - center);
  let horizontal = 1;
  if (dist > FADE_START) {
    const t = Math.min((dist - FADE_START) / (FADE_END - FADE_START), 1);
    horizontal = 1 - t * 0.97; // fades to 0.03
  }
  return horizontal * (ROW_OPACITY[ri] ?? 0.1);
}

function getDelay(ri: number, ci: number, rowLen: number): number {
  const centerCol = (rowLen - 1) / 2;
  const dr = ri - CENTER_ROW;
  const dc = ci - centerCol;
  return Math.min(Math.sqrt(dr * dr + dc * dc) * 40, 800);
}

// Simple hash to create pseudo-random but deterministic values per cell
function pseudoRandom(ri: number, ci: number): number {
  const x = Math.sin(ri * 127.1 + ci * 311.7) * 43758.5453;
  return x - Math.floor(x); // 0–1
}

/* ── Single hex tile ── */
function HexTile({
  cell,
  delay,
  opacity,
  skipAnim,
  ri,
  ci,
}: {
  cell: HexCell;
  delay: number;
  opacity: number;
  skipAnim: boolean;
  ri: number;
  ci: number;
}) {
  const isGhost = cell.variant === "ghost";
  const isAgent = cell.variant === "agent";

  // Use --color-border (light: #e5e5e5, dark: #262626) — clean in both themes
  const borderColor = isAgent ? "var(--color-fg-secondary)" : "var(--color-border)";
  const borderOpacity = isGhost ? 0.4 : isAgent ? 0.3 : 0.6;

  const fillColor = isAgent
    ? "var(--color-bg)"
    : "transparent";

  const tile = (
    <div
      className="relative flex-shrink-0 w-[60px] h-[69px] md:w-[72px] md:h-[83px] group/hex"
      style={{
        opacity,
        ...(skipAnim
          ? {}
          : {
              animation: `hexReveal 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms both`,
            }),
      }}
    >
      {/* Border layer */}
      <div
        className="absolute inset-0"
        style={{
          clipPath: HEX_CLIP,
          backgroundColor: borderColor,
          opacity: borderOpacity,
        }}
      />
      {/* Fill layer */}
      <div
        className="absolute inset-[1px] flex flex-col items-center justify-center"
        style={{
          clipPath: HEX_CLIP,
          backgroundColor: fillColor,
        }}
      >
        {cell.label && (
          <span
            className={cn(
              "font-[family-name:var(--font-mono)] font-medium leading-tight text-center",
              isAgent
                ? "text-[9px] md:text-[11px]"
                : "text-[7px] md:text-[9px] text-[var(--color-fg-secondary)]",
            )}
          >
            {cell.label}
          </span>
        )}
        {cell.sub && (
          <span className="text-[6px] md:text-[7px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] tracking-wider uppercase mt-0.5">
            {cell.sub}
          </span>
        )}
      </div>
      {/* Subtle breathe pulse — ~30% of non-ghost cells, random timing */}
      {!isGhost && !skipAnim && pseudoRandom(ri, ci) < 0.3 && (
        <div
          className="absolute inset-[-1px] pointer-events-none"
          style={{
            clipPath: HEX_CLIP,
            background: "var(--color-fg)",
            opacity: 0.03,
            animation: `hexBreathe ${3 + pseudoRandom(ci, ri) * 4}s ease-in-out ${pseudoRandom(ri + 1, ci + 1) * 5}s infinite`,
          }}
        />
      )}
      {/* Hover breathe — lights up on mouseover, fades on leave */}
      {!skipAnim && (
        <div
          className="absolute inset-[-1px] pointer-events-none hex-hover-glow opacity-0 group-hover/hex:opacity-[0.06]"
          style={{ clipPath: HEX_CLIP, background: "var(--color-fg)" }}
        />
      )}
    </div>
  );

  if (cell.href) {
    return (
      <Link to={cell.href} className="block hover:opacity-80 transition-opacity">
        {tile}
      </Link>
    );
  }
  return tile;
}

/* ── Hero ── */
export function PlatformHero() {
  const skipAnim = useReducedMotion();

  return (
    <section className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden">
      {/* Title + subtitle + nav */}
      <div className="relative z-20 max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            How we build
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            Built once. Deployed across every vertical. Compliance, governance, and infrastructure that every COSX agent inherits on day one.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] border-b border-[var(--color-border)] hover:border-[var(--color-fg-secondary)] transition-colors"
              >
                <span className={cn("w-1.5 h-1.5 rounded-full", link.dotColor)} />
                <span>{link.label}</span>
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      {/* Full-width honeycomb grid — negative margin pulls row 0 up behind hero text */}
      <div className="w-screen relative mt-8 md:mt-12 overflow-hidden hero-strip-mask" style={{ marginTop: "-52px" }}>
        <div className="py-1">
          {ROWS.map((row, ri) => (
            <div
              key={ri}
              className="relative flex w-max"
              style={{
                gap: "3px",
                left: "50%",
                transform: "translateX(-50%)",
                paddingLeft: ri % 2 === 1 ? "37.5px" : "0",
                marginTop: ri > 0 ? "-21px" : "0",
              }}
            >
              {row.map((cell, ci) => (
                <HexTile
                  key={`${ri}-${ci}`}
                  cell={cell}
                  delay={getDelay(ri, ci, row.length)}
                  opacity={getOpacity(ri, ci, row.length)}
                  skipAnim={skipAnim}
                  ri={ri}
                  ci={ci}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
